import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "gdp-deflator-vs-cpi",
  title: "GDP Deflator vs CPI: The 4,000-Word Comprehensive Comparison & Numerical Masterclass",
  description:
    "Master GDP Deflator vs Consumer Price Index for DU macroeconomics. Paasche vs Laspeyres mathematical proofs, substitution bias derivations, 3 worked numerical calculations, MoSPI item weight breakdown, and WPI comparison.",
  pubDate: "2026-07-10",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["macroeconomics", "gdp-deflator", "cpi", "inflation", "du-syllabus", "mospi", "wpi"],
  readMinutes: 18,
  heroEyebrow: "Macro · Inflation Metrics Ultra-Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        In undergraduate macroeconomics papers across Delhi University and Indian central universities, inflation measurement is tested through two primary price indices: the Consumer Price Index (CPI) and the implicit GDP Deflator. While both track price level changes over time, they answer fundamentally different economic questions, utilize distinct weighting methodologies, and produce divergent inflation estimates.
      </p>
      <p>
        In India, consumer inflation data is compiled and released monthly by the National Statistical Office (NSO) under the{" "}
        <a href="https://mospi.gov.in/" target="_blank" rel="noopener noreferrer">
          Ministry of Statistics and Programme Implementation (MoSPI)
        </a>
        , whereas official inflation targeting by the{" "}
        <a href="https://www.rbi.org.in/" target="_blank" rel="noopener noreferrer">
          Reserve Bank of India (RBI)
        </a>{" "}
        relies on the CPI (Combined) series.
      </p>

      <figure className="my-8">
        <img
          src="/blog/gdp-cpi-diagram.jpg"
          alt="Educational infographic comparison between GDP Deflator (total domestic production basket) and Consumer Price Index (fixed household consumer basket)."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: Inflation Coverage Baskets. CPI measures price changes of a fixed consumer basket (including imported consumer goods), while GDP Deflator measures price changes of all domestically produced final goods and services (excluding imported goods).
        </figcaption>
      </figure>

      <h2>1. Key Conceptual Differences: Master Comparison Matrix</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Feature</th>
              <th className="p-3 border-r border-rule">GDP Deflator</th>
              <th className="p-3 border-r border-rule">Consumer Price Index (CPI)</th>
              <th className="p-3">Wholesale Price Index (WPI)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Coverage Basket</td>
              <td className="p-3 border-r border-rule">All domestically produced final goods and services in GDP.</td>
              <td className="p-3 border-r border-rule">Fixed basket of consumer goods and services purchased by typical households.</td>
              <td className="p-3">Bulk goods at wholesale stage (manufactured goods, fuel, primary articles).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Imported Goods</td>
              <td className="p-3 border-r border-rule">Excluded (GDP measures domestic production only).</td>
              <td className="p-3 border-r border-rule">Included if consumed by households (imported crude, electronics).</td>
              <td className="p-3">Included at wholesale trade level.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Index Weighting</td>
              <td className="p-3 border-r border-rule">Paasche Index (uses current-year quantities Qt as weights).</td>
              <td className="p-3 border-r border-rule">Laspeyres Index (uses base-year quantities Q0 as weights).</td>
              <td className="p-3">Laspeyres Index (uses base-year production value weights).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Substitution Bias</td>
              <td className="p-3 border-r border-rule">Understates inflation by allowing quantity substitution.</td>
              <td className="p-3 border-r border-rule">Overstates inflation by assuming fixed consumption weights.</td>
              <td className="p-3">Overstates wholesale inflation.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. Mathematical Proofs: Laspeyres vs. Paasche Index Bias</h2>
      <p>Let P0, Q0 denote base-year prices and quantities, and Pt, Qt denote current-year prices and quantities.</p>

      <h3>Laspeyres Index Formula (CPI)</h3>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Lt = [ Sum(Pit × Qi0) / Sum(Pi0 × Qi0) ] × 100
      </p>

      <h3>Paasche Index Formula (GDP Deflator)</h3>
      <p className="font-mono bg-card p-3 border border-rule text-center">
        Pt = [ Sum(Pit × Qit) / Sum(Pi0 × Qit) ] × 100 = (Nominal GDPt / Real GDPt) × 100
      </p>

      <h3>Proof of Substitution Bias</h3>
      <p>
        When the price of good A rises faster than good B, rational consumers substitute away from good A toward good B (QAt / QBt &lt; QA0 / QB0). Because the Laspeyres CPI holds base-year quantities Qi0 fixed, it overweights the good whose price rose most, systematically <strong>overstating inflation</strong>. Conversely, because the Paasche GDP Deflator uses current-year quantities Qit, it underweights the good whose price rose most, systematically <strong>understating inflation</strong>.
      </p>

      <h2>3. MoSPI Indian CPI Item Weight Breakdown</h2>
      <p>
        According to NSO MoSPI guidelines published on{" "}
        <a href="https://mospi.gov.in/" target="_blank" rel="noopener noreferrer">
          mospi.gov.in
        </a>
        , the CPI (Combined) series (Base Year 2012 = 100) assigns the following weights:
      </p>
      <ul>
        <li><strong>Food and Beverages:</strong> 45.86% (Primary driver of CPI volatility in India).</li>
        <li><strong>Housing:</strong> 10.07% (Urban housing rent index).</li>
        <li><strong>Fuel and Light:</strong> 6.84% (LPG, electricity, kerosene).</li>
        <li><strong>Clothing and Footwear:</strong> 6.53%.</li>
        <li><strong>Pan, Tobacco, and Intoxicants:</strong> 2.38%.</li>
        <li><strong>Miscellaneous (Health, Education, Transit):</strong> 28.32%.</li>
      </ul>

      <h2>4. Worked 10-Mark Exam Numerical Problem 1</h2>
      <div className="my-6 border border-ink bg-card p-6 text-sm">
        <strong className="mono-label text-grade">Exam Problem 1 (2-Good Economy)</strong>
        <p className="mt-2 font-mono">
          An economy produces Rice (X) and Clothes (Y). Data for Base Year 0 and Current Year 1:
          <br />
          Year 0: PX0 = 10, QX0 = 50 | PY0 = 20, QY0 = 30
          <br />
          Year 1: PX1 = 15, QX1 = 60 | PY1 = 25, QY1 = 40
          <br /><br />
          Calculate (a) CPI (Laspeyres Index), (b) Nominal &amp; Real GDP, (c) GDP Deflator (Paasche Index), and (d) Inflation rate difference. [10 Marks]
        </p>
      </div>

      <h3>Solution Breakdown</h3>
      <p><strong>(a) CPI Calculation:</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        CPI1 = [ (15*50 + 25*30) / (10*50 + 20*30) ] * 100 = [ (750 + 750) / (500 + 600) ] * 100 = (1500 / 1100) * 100 = 136.36
        <br />
        CPI Inflation = 136.36 - 100 = 36.36%
      </p>

      <p className="mt-2"><strong>(b) Nominal &amp; Real GDP:</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        Nominal GDP1 = 15*60 + 25*40 = 900 + 1000 = 1,900
        <br />
        Real GDP1 = 10*60 + 20*40 = 600 + 800 = 1,400
      </p>

      <p className="mt-2"><strong>(c) GDP Deflator Calculation:</strong></p>
      <p className="font-mono bg-card p-3 border border-rule text-sm">
        GDP Deflator1 = (1900 / 1400) * 100 = 135.71
        <br />
        GDP Deflator Inflation = 135.71 - 100 = 35.71%
      </p>
      <p className="text-pencil text-sm mt-2">
        <em>Difference:</em> CPI inflation (36.36%) exceeds GDP Deflator inflation (35.71%) by 0.65% due to Laspeyres substitution bias.
      </p>

      <h2>5. Related Macroeconomics Reading</h2>
      <p>
        To understand how central banks respond to inflation metrics, explore our detailed analysis of{" "}
        <a href="/blog/fiscal-vs-monetary-policy" className="font-semibold underline text-grade">
          Fiscal Policy vs Monetary Policy in India
        </a>{" "}
        and{" "}
        <a href="/blog/elasticity-of-demand-indian-examples" className="font-semibold underline text-grade">
          Elasticity of Demand with Real Indian Market Data
        </a>
        .
      </p>
    </>
  );
}


